import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
} from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { debounceTime, distinctUntilChanged } from 'rxjs';

import { InputValidationService } from '../../../@core/services/input-validation.service';
import { SafeAny } from '../../../types/safe-any';
import { EditItemProcessDialogComponent } from '../edit-item-process-dialog/edit-item-process-dialog.component';
import { IdOdValidationDialogComponent } from '../id-od-validation-dialog/id-od-validation-dialog.component';

export interface QuoteDetail {
  item: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  uniqueId: string;
  lbPer1000ft: number;
  basePrice: number;
  surcharge: number;
  units?: number;
  innerDiameter?: number;
  outerDiameter?: number;
  ftNeeded?: number;
  totalLbs?: number;
  quantityBreakMargin?: number;
  setup?: number;
  sellPrice?: number;
  isParent?: boolean;
  itemType?: string;
  specificGravity?: number;
  uniqueIdentifier?: string;
  [key: string]: string | number | boolean | undefined;
}

@Component({
  selector: 'ciwc-quote-detail-accordion',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './quote-detail-accordion.component.html',
  styleUrl: './quote-detail-accordion.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteDetailAccordionComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChildren('numericInput') numericInputs!: QueryList<ElementRef>;

  @Input() title = '';
  @Input() key = '';
  @Input() color = '#a93b38';
  @Input() data: QuoteDetail[] = [];
  @Input() quantityBreaks: { quantity: number; pieces: number }[] | null = null;
  @Input() readOnly = false;
  @Output() deleteItem = new EventEmitter<{ item: QuoteDetail; key: string }>();
  @Output() updateItem = new EventEmitter<{ item: QuoteDetail; key: string }>();
  @Output() fieldUpdate = new EventEmitter<{
    item: QuoteDetail;
    key: string;
    field: string;
    value: SafeAny;
  }>();
  @Output() dataSourceChange = new EventEmitter<{ data: QuoteDetail[]; itemType: string }>();

  dataSource: QuoteDetail[] = [];
  displayedColumns: string[] = [];
  form: FormGroup;

  private readonly columnConfigs: Record<string, string[]> = {
    OTHER: [
      'rowNumber',
      'description',
      'uniqueIdentifier',
      'pricePerUnit',
      'units',
      'ftNeeded',
      'quantityBreakMargin',
      'setup',
      'sellPrice',
      'actions',
    ],
    COPPER: [
      'rowNumber',
      'description',
      'uniqueIdentifier',
      'lbPer1000ft',
      'pricePerLb',
      'units',
      'ftNeeded',
      'totalLbs',
      'quantityBreakMargin',
      'setup',
      'sellPrice',
      'actions',
    ],
    SINGLE_END: [
      'rowNumber',
      'description',
      'uniqueIdentifier',
      'innerDiameter',
      'pricePerLb',
      'units',
      'ftNeeded',
      'totalLbs',
      'quantityBreakMargin',
      'setup',
      'sellPrice',
      'actions',
    ],
    COMPOUNDS: [
      'rowNumber',
      'description',
      'uniqueIdentifier',
      'innerDiameter',
      'outerDiameter',
      'pricePerLb',
      'units',
      'ftNeeded',
      'totalLbs',
      'quantityBreakMargin',
      'setup',
      'sellPrice',
      'actions',
    ],
    TEFLON_TAPES: [
      'rowNumber',
      'description',
      'uniqueIdentifier',
      'innerDiameter',
      'pricePerLb',
      'units',
      'ftNeeded',
      'totalLbs',
      'quantityBreakMargin',
      'setup',
      'sellPrice',
      'actions',
    ],
    ALL_TAPES_AND_FILLERS: [
      'rowNumber',
      'description',
      'uniqueIdentifier',
      'innerDiameter',
      'pricePerLb',
      'units',
      'ftNeeded',
      'totalLbs',
      'quantityBreakMargin',
      'setup',
      'sellPrice',
      'actions',
    ],
  };

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {
    this.form = this.fb.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['key'] || changes['data'] || changes['readOnly']) {
      let columns = this.columnConfigs[this.key] || [];

      // Remove actions column in read-only mode
      if (this.readOnly) {
        columns = columns.filter((col) => col !== 'actions');
      }

      this.displayedColumns = columns;
      this.dataSource = [...this.data];

      // Only initialize form if not in read-only mode
      if (!this.readOnly) {
        this.initializeForm();
      }
    }

    if (changes['quantityBreaks'] && this.quantityBreaks) {
      // Recalculate values for all items when quantity breaks change
      this.dataSource.forEach((item) => {
        this.calculateValues(item);
      });
    }
  }

  private initializeForm(): void {
    const group: Record<string, FormControl<string | number | null>> = {};
    this.dataSource.forEach((item, index) => {
      // Add form controls for Units, Qty Break Margin, ID, Setup, and OD
      const fields = [
        'units',
        'quantityBreakMargin',
        'innerDiameter',
        'setup',
        'outerDiameter',
        'pricePerUnit',
      ] as const;
      fields.forEach((field) => {
        const value = item[field];
        const control = new FormControl<string | number | null>(
          typeof value === 'number' && value !== 0 ? value : '',
        );
        group[`${field}_${index}`] = control;

        // Subscribe to value change
        let valueChanges$ = control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));

        // Add debounce time for ID/OD fields in COMPOUNDS type
        if (this.key === 'COMPOUNDS' && (field === 'innerDiameter' || field === 'outerDiameter')) {
          valueChanges$ = valueChanges$.pipe(debounceTime(500), distinctUntilChanged());
        }

        valueChanges$.subscribe((value) => {
          const numValue = Number(value);

          if (!isNaN(numValue)) {
            const fieldKey = field as keyof QuoteDetail;
            this.dataSource[index][fieldKey] = numValue;

            // Check ID/OD validation when either value changes for COMPOUNDS type
            if (
              (field === 'innerDiameter' || field === 'outerDiameter') &&
              this.key === 'COMPOUNDS' &&
              this.dataSource[index].innerDiameter !== null &&
              this.dataSource[index].innerDiameter !== undefined &&
              this.dataSource[index].outerDiameter !== null &&
              this.dataSource[index].outerDiameter !== undefined
            ) {
              const currentItem = this.dataSource[index];
              const id = Number(currentItem.innerDiameter) || 0;
              const od = Number(currentItem.outerDiameter) || 0;

              if (id >= od) {
                this.dialog.open(IdOdValidationDialogComponent, {
                  width: '400px',
                  disableClose: true,
                });
              }
            }

            // If units changed, recalculate all items
            if (field === 'units') {
              this.dataSource.forEach((dataItem) => {
                this.calculateValues(dataItem);
              });
            } else if (
              field === 'quantityBreakMargin' ||
              field === 'setup' ||
              field === 'innerDiameter' ||
              field === 'outerDiameter' ||
              field === 'pricePerUnit'
            ) {
              this.calculateValues(this.dataSource[index]);
            }
          }
        });
      });
    });
    this.form = this.fb.group(group);
  }

  private calculateValues(item: QuoteDetail): void {
    let parentUnits: number | undefined;
    let parentInnerDiameter: number | undefined;
    let parentOuterDiameter: number | undefined;

    if (!item.isParent) {
      // Get the base uniqueId by removing the suffix (e.g. "_1", "_2", etc.)
      const baseUniqueId = item.uniqueId?.split('_')[0];
      // Find the parent item with matching base uniqueId
      const parentItem = this.dataSource.find((d) => d.uniqueId == baseUniqueId && d.isParent);
      if (parentItem) {
        parentUnits = parentItem.units;
        parentInnerDiameter = parentItem.innerDiameter;
        parentOuterDiameter = parentItem.outerDiameter;
        // For ALL_TAPES_AND_FILLERS, we need these additional parent values
        if (item.itemType === 'ALL_TAPES_AND_FILLERS') {
          item.specificGravity = parentItem.specificGravity;
          item['constant1'] = parentItem['constant1'];
          item['constant2'] = parentItem['constant2'];
          item['pricePerLb'] = parentItem['pricePerLb'];
        }
      }
    }

    const units = item.isParent ? Number(item.units) : parentUnits || 0;
    const ftNeeded = Number(item.ftNeeded) || 0;
    const setup = Number(item.setup) || 0;
    const quantityBreakMargin = Number(item.quantityBreakMargin) || 0;

    switch (item.itemType) {
      case 'COPPER': {
        const lbPer1000ft = Number(item.lbPer1000ft) || 1;
        const pricePerLb = Number(item['pricePerLb']) || 1;

        item.totalLbs = (lbPer1000ft * ftNeeded * units) / 1000;
        item.sellPrice = item.totalLbs * pricePerLb * ftNeeded * quantityBreakMargin + setup;
        item.sellPrice = parseFloat(
          (
            ((units * lbPer1000ft * ftNeeded) / 1000) * pricePerLb * quantityBreakMargin +
            setup
          ).toFixed(2),
        );
        break;
      }
      case 'OTHER': {
        const pricePerUnit = Number(item['pricePerUnit']) || 0;

        item.sellPrice = units * pricePerUnit * quantityBreakMargin + setup;
        break;
      }

      case 'SINGLE_END': {
        const specificGravity = Number(item.specificGravity) || 0;
        const innerDiameter = Number(item.innerDiameter) || parentInnerDiameter || 0;
        const pricePerLb = Number(item['pricePerLb']) || 0;
        const constant1 = Number(item['constant1']) || 0;
        item.totalLbs = ((innerDiameter + constant1) * ftNeeded * units * specificGravity) / 1000;
        item.sellPrice =
          ((innerDiameter + constant1) *
            specificGravity *
            ftNeeded *
            units *
            quantityBreakMargin *
            pricePerLb) /
            1000 +
          setup;
        break;
      }

      case 'COMPOUNDS': {
        const specificGravity = Number(item.specificGravity) || 0;
        const innerDiameter = Number(item.innerDiameter) || parentInnerDiameter || 0;
        const outerDiameter = Number(item.outerDiameter) || parentOuterDiameter || 0;
        const pricePerLb = Number(item['pricePerLb']) || 0;

        item.totalLbs =
          (((Math.pow(outerDiameter, 2) - Math.pow(innerDiameter, 2)) / 2) *
            680 *
            specificGravity *
            ftNeeded *
            units) /
          1000;
        item.sellPrice =
          (((Math.pow(outerDiameter, 2) - Math.pow(innerDiameter, 2)) / 2) *
            680 *
            specificGravity *
            ftNeeded *
            units *
            quantityBreakMargin *
            pricePerLb) /
            1000 +
          setup;
        break;
      }

      case 'TEFLON_TAPES': {
        const specificGravity = Number(item.specificGravity) || 0;
        const innerDiameter = Number(item.innerDiameter) || parentInnerDiameter || 0;
        const pricePerLb = Number(item['pricePerLb']) || 0;
        item.totalLbs =
          ((innerDiameter + 0.011) * 10.912 * specificGravity * ftNeeded * units) / 1000;
        item.sellPrice =
          ((innerDiameter + 0.011) *
            10.912 *
            specificGravity *
            ftNeeded *
            units *
            quantityBreakMargin *
            pricePerLb) /
            1000 +
          (setup || 0);
        break;
      }

      case 'ALL_TAPES_AND_FILLERS': {
        const specificGravity = Number(item.specificGravity) || 0;
        const innerDiameter = Number(item.innerDiameter) || parentInnerDiameter || 0;
        const pricePerLb = Number(item['pricePerLb']) || 0;
        const constant1 = Number(item['constant1']) || 0;
        const constant2 = Number(item['constant2']) || 0;

        // Calculate total lbs
        const totalLbsCalc =
          ((2 * innerDiameter + constant1 + constant2) * specificGravity * ftNeeded * units) / 1000;
        item.totalLbs = parseFloat(totalLbsCalc.toFixed(2));

        // Calculate sell price
        const sellPriceCalc = totalLbsCalc * pricePerLb * quantityBreakMargin + setup;
        item.sellPrice = parseFloat(sellPriceCalc.toFixed(2));
        break;
      }
    }

    this.dataSourceChange.emit({ data: this.dataSource, itemType: this.key });
  }

  onDelete(item: QuoteDetail): void {
    this.deleteItem.emit({ item, key: this.key });

    // Emit delete events for any child items that were deleted
    const deletedUniqueId = item.uniqueId;

    const childItems = this.data.filter((dataItem) => {
      if (typeof dataItem['uniqueId'] === 'string') {
        return dataItem['uniqueId'].startsWith(deletedUniqueId + '_');
      }
      return false;
    });

    childItems.forEach((childItem) => {
      this.deleteItem.emit({ item: childItem, key: this.key });
    });

    this.data = this.data.filter((dataItem) => dataItem.uniqueId !== item.uniqueId);
    // Remove any child items that have the parent's uniqueId as a prefix
    this.data = this.data.filter((dataItem) => {
      if (typeof dataItem.uniqueId !== 'string') {
        return true; // Keep items with non-string uniqueIds
      }

      const uniqueIdParts = dataItem.uniqueId.split('_');
      // Keep items that don't have an underscore, or don't match the deleted item's base ID

      return uniqueIdParts.length === 1 || uniqueIdParts[0] !== item.uniqueId;
    });

    this.dataSource = [...this.data];
  }

  onEdit(item: QuoteDetail): void {
    const dialogRef = this.dialog.open(EditItemProcessDialogComponent, {
      data: {
        title: this.title,
        item,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updatedItem = {
          ...item,
          ...result,
        };

        this.calculateAllValues(updatedItem);
        this.updateItem.emit({ item: updatedItem, key: this.key });
      }
    });
  }

  showCellContent(row: QuoteDetail, cellType: string): boolean {
    if (row?.isParent) {
      return true;
    }

    if (cellType === 'totalLbs') {
      return row.itemType !== 'OTHER';
    }

    if (cellType === 'pricePerUnit') {
      return row.itemType === 'OTHER';
    }

    return false;
  }

  private calculateAllValues(item: QuoteDetail): void {
    this.calculateValues(item);

    // Check if item is a parent
    if (item.isParent) {
      // Find children: items whose uniqueId starts with the parent's uniqueId + '_' and are not parent themselves
      const parentId = item.uniqueId;
      const children = this.dataSource.filter(
        (item) =>
          item.uniqueId &&
          typeof item.uniqueId === 'string' &&
          item.uniqueId.startsWith(parentId + '_') &&
          !item.isParent,
      );

      if (children.length > 0) {
        const copperFields = ['lbPer1000ft', 'pricePerLb'];
        const otherFields = ['pricePerUnit'];
        const singleEndFields = ['specificGravity', 'constant1', 'pricePerLb'];
        const compoundsFields = ['specificGravity', 'pricePerLb'];
        const teflonTapesFields = ['specificGravity', 'pricePerLb'];
        const allTapesAndFillersFields = [
          'specificGravity',
          'constant1',
          'constant2',
          'pricePerLb',
        ];

        // Update child properties based on itemType
        children.forEach((child) => {
          switch (item.itemType) {
            case 'COPPER':
              copperFields.forEach((field) => {
                child[field] = item[field];
              });
              break;
            case 'OTHER':
              otherFields.forEach((field) => {
                child[field] = item[field];
              });
              break;
            case 'SINGLE_END':
              singleEndFields.forEach((field) => {
                child[field] = item[field];
              });
              break;
            case 'COMPOUNDS':
              compoundsFields.forEach((field) => {
                child[field] = item[field];
              });
              break;
            case 'TEFLON_TAPES':
              teflonTapesFields.forEach((field) => {
                child[field] = item[field];
              });
              break;
            case 'ALL_TAPES_AND_FILLERS':
              allTapesAndFillersFields.forEach((field) => {
                child[field] = item[field];
              });
              break;
            // Add more cases here if needed for other item types
            default:
              break;
          }
          this.calculateValues(child);
        });
      }
    }
  }

  readonly onNumericKeyPress = inject(InputValidationService).onNumericKeyPress;
}
